import {
  ColorsConfigDTO,
  DashboardDTO,
  DashboardLayoutsDTO,
  LayoutSettingsDTO,
  ListDashboardsDashboardTypeEnum,
  NewDashboardDTO,
  NewDashboardDTOTypeEnum,
  NewVersionDashboardDTO,
  NewVersionDashboardDTOTypeEnum,
  UpdateDashboardDTO,
  UpdateDashboardDTOTypeEnum,
  WidgetDTO,
} from '@neptune/shared/core-apis-leaderboard-domain';
import type { GlyphName } from '@neptune/shared/core-glyphs-domain';
import { DashboardConfigModelConverter } from '@neptune/shared/dashboard-domain';
import { TableViewParamsConverter } from '@neptune/shared/leaderboard-domain';
import {
  DEFAULT_WIDGET_LAYOUT_SETTINGS,
  GlobalWidgetLayoutSettings,
  gridModeColumnsCountFromApiToDomain,
  Widget,
  widgetLayoutModeFromApiToDomain,
  widgetLayoutModeFromDomainToApi,
} from '@neptune/shared/widgets-domain';

import { dashboardIcons } from 'domain/dashboard/dashboard-icons';
import {
  Dashboard,
  DashboardType,
  NewDashboard,
  NewVersionDashboard,
  WidgetLayouts,
} from 'domain/dashboard/dashboard-model';
import { WidgetModelConverter } from 'domain/widget';

export const UNTITLED_DASHBOARD_NAME = 'Untitled Dashboard';

const newDashboardTypeMapping: Record<DashboardType, NewDashboardDTOTypeEnum> = {
  compare: NewDashboardDTOTypeEnum.Compare,
  experiment: NewDashboardDTOTypeEnum.Experiment,
  report: NewDashboardDTOTypeEnum.Report,
};

const updateDashboardTypeMapping: Record<DashboardType, UpdateDashboardDTOTypeEnum> = {
  compare: UpdateDashboardDTOTypeEnum.Compare,
  experiment: UpdateDashboardDTOTypeEnum.Experiment,
  report: UpdateDashboardDTOTypeEnum.Report,
};

const newVersionTypeMapping: Record<DashboardType, NewVersionDashboardDTOTypeEnum> = {
  compare: NewVersionDashboardDTOTypeEnum.Compare,
  experiment: NewVersionDashboardDTOTypeEnum.Experiment,
  report: NewVersionDashboardDTOTypeEnum.Report,
};

const listDashboardTypeMapping: Record<DashboardType, ListDashboardsDashboardTypeEnum> = {
  compare: ListDashboardsDashboardTypeEnum.Compare,
  experiment: ListDashboardsDashboardTypeEnum.Experiment,
  report: ListDashboardsDashboardTypeEnum.Report,
};

export abstract class DashboardModelConverter {
  static dashboardFromApiToDomain(dashboard: DashboardDTO): Dashboard {
    return {
      ...dashboard,
      config: DashboardConfigModelConverter.fromApiToDomain(dashboard.config),
      gridLayouts: DashboardModelConverter.gridLayoutsFromApiToDomain(dashboard.layouts),
      versionBranchId: dashboard.versionBranch,
      icon: DashboardModelConverter.dashboardIconFromApiToDomain(dashboard.icon),
      widgets: dashboard.widgets.map(WidgetModelConverter.widgetFromApiToDomain),
      viewParams: dashboard.viewParams
        ? TableViewParamsConverter.fromApiToDomain(dashboard.viewParams)
        : undefined,
      colorsConfig: DashboardModelConverter.colorsFromApiToDomain(dashboard.colorsConfig),
      widgetLayoutSettings: DashboardModelConverter.widgetLayoutFromApiToDomain(
        dashboard.layoutSettings,
      ),
    };
  }

  static dashboardIconFromApiToDomain(icon: string | undefined): GlyphName | undefined {
    if (!dashboardIcons.includes(icon as GlyphName)) {
      return undefined;
    }

    return icon as GlyphName;
  }

  static gridLayoutsFromApiToDomain(layouts: DashboardLayoutsDTO): WidgetLayouts {
    // isStatic is a legacy field. We remove it here to ensure data stability in domain objects,
    // so that they can be compared with isEqual.
    return layouts.md.map(({ isStatic, ...rest }) => rest);
  }

  static gridLayoutsFromDomainToApi(layouts: WidgetLayouts): DashboardLayoutsDTO {
    return {
      xs: [], // required for legacy reasons
      md: layouts,
      lg: [], // required for legacy reasons
      sm: [], // required for legacy reasons
      xl: [], // required for legacy reasons
    };
  }

  static dashboardWidgetsFromDomainToApi(widgets: Widget[]): WidgetDTO[] {
    return widgets
      .map(WidgetModelConverter.widgetFromDomainToApi)
      .filter((widget): widget is WidgetDTO => widget != null);
  }

  static newDashboardFromDomainToApi(dashboard: NewDashboard): NewDashboardDTO {
    const { experimentsOnly, runsLineage, ...rest } = dashboard;
    return {
      ...rest,
      config: DashboardConfigModelConverter.fromDomainToApi(dashboard.config),
      layouts: DashboardModelConverter.gridLayoutsFromDomainToApi(dashboard.gridLayouts),
      type: DashboardModelConverter.newDashboardTypeFromDomainToApi(dashboard.type),
      widgets: DashboardModelConverter.dashboardWidgetsFromDomainToApi(dashboard.widgets),
      viewParams: dashboard.viewParams
        ? TableViewParamsConverter.fromDomainToApi(dashboard.viewParams)
        : undefined,
      colorsConfig: DashboardModelConverter.colorsFromDomainToApi(dashboard.colorsConfig),
    };
  }

  static newDashboardTypeFromDomainToApi(type: DashboardType): NewDashboardDTOTypeEnum {
    return newDashboardTypeMapping[type];
  }

  static updateDashboardTypeFromDomainToApi(type: DashboardType): UpdateDashboardDTOTypeEnum {
    return updateDashboardTypeMapping[type];
  }

  static updateDashboardFromDomainToApi(dashboard: Dashboard): UpdateDashboardDTO {
    return {
      ...dashboard,
      name: dashboard.name || UNTITLED_DASHBOARD_NAME,
      config: DashboardConfigModelConverter.fromDomainToApi(dashboard.config),
      layouts: DashboardModelConverter.gridLayoutsFromDomainToApi(dashboard.gridLayouts),
      type: DashboardModelConverter.updateDashboardTypeFromDomainToApi(dashboard.type),
      widgets: DashboardModelConverter.dashboardWidgetsFromDomainToApi(dashboard.widgets),
      viewParams: dashboard.viewParams
        ? TableViewParamsConverter.fromDomainToApi(dashboard.viewParams)
        : undefined,
      colorsConfig: DashboardModelConverter.colorsFromDomainToApi(dashboard.colorsConfig),
      layoutSettings: DashboardModelConverter.widgetLayoutFromDomainToApi(
        dashboard.widgetLayoutSettings,
      ),
    };
  }

  static newVersionDashboardFromDomainToApi(
    dashboard: NewVersionDashboard,
  ): NewVersionDashboardDTO {
    return {
      ...dashboard,
      name: dashboard.name || UNTITLED_DASHBOARD_NAME,
      config: DashboardConfigModelConverter.fromDomainToApi(dashboard.config),
      layouts: DashboardModelConverter.gridLayoutsFromDomainToApi(dashboard.gridLayouts),
      versionBranch: dashboard.versionBranchId,
      type: DashboardModelConverter.newVersionTypeFromDomainToApi(dashboard.type),
      widgets: DashboardModelConverter.dashboardWidgetsFromDomainToApi(dashboard.widgets),
      viewParams: dashboard.viewParams
        ? TableViewParamsConverter.fromDomainToApi(dashboard.viewParams)
        : undefined,
      colorsConfig: DashboardModelConverter.colorsFromDomainToApi(dashboard.colorsConfig),
      layoutSettings: DashboardModelConverter.widgetLayoutFromDomainToApi(
        dashboard.widgetLayoutSettings,
      ),
    };
  }

  static newVersionTypeFromDomainToApi(type: DashboardType): NewVersionDashboardDTOTypeEnum {
    return newVersionTypeMapping[type];
  }

  static listDashboardTypeFromDomainToApi(): ListDashboardsDashboardTypeEnum {
    // casting is a workaround for Enum[] generated as Enum in our TS client (bug in swagger generation)
    return [
      listDashboardTypeMapping['compare'],
      listDashboardTypeMapping['experiment'],
    ] as unknown as ListDashboardsDashboardTypeEnum;
  }

  static colorsFromApiToDomain(colorsConfig?: ColorsConfigDTO): Record<string, string> | undefined {
    return colorsConfig?.colors?.reduce<Record<string, string>>(
      (res, { owner, color }) => ((res[owner] = color), res),
      {},
    );
  }

  static colorsFromDomainToApi(colors?: Record<string, string>): ColorsConfigDTO | undefined {
    if (!colors) {
      return undefined;
    }

    return {
      colors: Object.entries(colors).map(([owner, color]) => ({ owner, color })),
    };
  }

  static widgetLayoutFromApiToDomain(layout?: LayoutSettingsDTO): GlobalWidgetLayoutSettings {
    if (!layout) {
      return DEFAULT_WIDGET_LAYOUT_SETTINGS;
    }

    return {
      mode: widgetLayoutModeFromApiToDomain(layout.mode),
      config: {
        gridMode: {
          columnsCount: gridModeColumnsCountFromApiToDomain(layout.config.gridMode.columnsCount),
        },
      },
    };
  }

  static widgetLayoutFromDomainToApi(layout: GlobalWidgetLayoutSettings): LayoutSettingsDTO {
    return {
      ...DEFAULT_WIDGET_LAYOUT_SETTINGS,
      ...layout,
      mode: widgetLayoutModeFromDomainToApi(layout.mode),
    };
  }
}
